import React, { useEffect, useState } from "react";
import { apiDelReqList, apiDelReqApprove, apiDelReqDeny } from "./api";

// =============================================================
// DeletionRequestPanel — Super Admin only. Lists pending +
// resolved deletion requests, allows Approve / Deny. Approvals
// counter shows progress out of 3.
// =============================================================
export default function DeletionRequestPanel({ currentUser, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denialFor, setDenialFor] = useState(null);
  const [denialReason, setDenialReason] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await apiDelReqList();
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e?.message || "Failed to load deletion requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await apiDelReqApprove(id);
      await load();
      onChange && onChange();
    } catch (e) { alert(e?.message || "Approve failed"); }
  };
  const deny = async () => {
    if (!denialReason.trim()) { alert("Denial reason is required."); return; }
    try {
      await apiDelReqDeny(denialFor, denialReason);
      setDenialFor(null); setDenialReason("");
      await load();
      onChange && onChange();
    } catch (e) { alert(e?.message || "Deny failed"); }
  };

  return (
    <div data-testid="deletion-request-panel" style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Deletion Requests
        </div>
        <button className="btn btn-sm btn-outline" onClick={load} data-testid="del-refresh">Refresh</button>
      </div>
      {loading && <div style={{ color: "var(--kc-sub)", fontSize: 12 }}>Loading…</div>}
      {error && <div style={{ color: "#ff6b6b", fontSize: 12 }} data-testid="del-error">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div data-testid="del-empty" style={{ padding: 20, textAlign: "center", color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
          No deletion requests.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(r => {
          const already = (r.approvals || []).some(a => a.userId === currentUser?.id);
          const isRequester = r.requestedBy === currentUser?.id;
          const isPending = r.status === "pending";
          return (
            <div key={r.id} data-testid={`del-req-${r.id}`} style={{
              background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 10, padding: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: "var(--kc-text)", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
                  {r.targetType}: <code style={{ color: "var(--kc-accent)" }}>{r.targetId}</code>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  padding: "3px 10px", borderRadius: 999,
                  color: r.status === "pending" ? "#f59e0b" : r.status === "approved" ? "#4ade80" : r.status === "expired" ? "var(--kc-sub)" : "#ff6b6b",
                  border: `1px solid ${r.status === "pending" ? "rgba(245,158,11,0.40)" : r.status === "approved" ? "rgba(74,222,128,0.40)" : "rgba(255,107,107,0.40)"}`,
                }}>{r.status} — {(r.approvals || []).length}/3</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--kc-sub)" }}>
                Reason: <em>{r.reason}</em>
              </div>
              <div style={{ marginTop: 4, fontSize: 10, color: "var(--kc-meta)" }}>
                Requested by {r.requestedByName || r.requestedBy} · {(r.requestedAt || "").slice(0, 10)}
              </div>
              {r.denial && (
                <div style={{ marginTop: 6, fontSize: 11, color: "#ff6b6b" }}>
                  Denied by {r.denial.name}: {r.denial.reason}
                </div>
              )}
              {isPending && !already && !isRequester && (
                <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                  <button className="btn btn-sm" data-testid={`del-approve-${r.id}`} onClick={() => approve(r.id)} style={{ background: "rgba(74,222,128,0.20)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.40)" }}>Approve</button>
                  <button className="btn btn-sm" data-testid={`del-deny-${r.id}`} onClick={() => setDenialFor(r.id)} style={{ background: "rgba(255,107,107,0.20)", color: "#ff6b6b", border: "1px solid rgba(255,107,107,0.40)" }}>Deny</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {denialFor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 210, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setDenialFor(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 12, padding: 20, maxWidth: 420, width: "100%" }}>
            <div style={{ fontFamily: "'Cinzel',serif", color: "var(--kc-accent)", fontWeight: 700, marginBottom: 12 }}>Deny Deletion Request</div>
            <textarea
              data-testid="del-deny-reason"
              value={denialReason}
              onChange={(e) => setDenialReason(e.target.value)}
              placeholder="Reason for denying (required)"
              className="form-input"
              style={{ width: "100%", minHeight: 80, marginBottom: 12 }}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-outline" onClick={() => { setDenialFor(null); setDenialReason(""); }}>Cancel</button>
              <button className="btn btn-sm" data-testid="del-deny-confirm" onClick={deny} style={{ background: "#ff6b6b", color: "#111", border: "1px solid #ff6b6b" }}>Deny Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
